/**
 * Events Discovery Page - Netflix/Spotify-Style Redesign
 * Curated Sections with Smart Chips and Infinite Scroll
 */

import { dialog } from '../organizer/event/modules/shared.js';
import Http from '../http/utils.js';
import { icon } from '../shared/icons/render.js';

// Get initial data from server
const INITIAL_DATA = window.CITA_EVENTS_DATA || {
  isLoggedIn: false,
  urlParams: {}
};

// ============================================================
// UTILITIES
// ============================================================

const Formatters = {
  formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${days[date.getDay()]}, ${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
  },

  formatDateBadge(dateStr) {
    if (!dateStr) return { day: '', month: '', time: '' };
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return {
      day: date.getDate(),
      month: months[date.getMonth()].toUpperCase(),
      time: `${hours}:${minutes}`
    };
  },

  formatPrice(price) {
    if (!price || price === 0) return null;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  },

  formatPriceHtml(price) {
    if (!price || price === 0) return '';
    const fmt = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    return `${fmt.format(price)}<span class="event-card-price-currency">&thinsp;&euro;</span>`;
  },

  truncate(text, maxLength = 100) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }
};

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function isEventToday(dateStr) {
  if (!dateStr) return false;
  const eventDate = new Date(dateStr);
  const today = new Date();
  return eventDate.toDateString() === today.toDateString();
}

function isEventSoon(dateStr) {
  if (!dateStr) return false;
  const eventDate = new Date(dateStr);
  const now = new Date();
  const hoursUntil = (eventDate - now) / (1000 * 60 * 60);
  return hoursUntil > 0 && hoursUntil <= 24;
}

function isMultiDay(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return false;
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  return startDate.toDateString() !== endDate.toDateString();
}

function getDurationDays(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return 1;
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1;
}

/**
 * Get SVG icon for event category
 * Returns inline SVG with consistent styling
 */
function getCategoryIcon(categoryName) {
  const name = (categoryName || '').toLowerCase().trim();

  // SVG base attributes for consistency
  const svgAttrs = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';

  // Category → SVG path mapping
  const icons = {
    // Musik & Entertainment
    'musik': `${icon('music')}`,
    'music': `${icon('music')}`,
    'konzert': `${icon('music')}`,
    'party': `<svg ${svgAttrs}><path d="M5.8 11.3L2 22l10.7-3.8M15 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/><path d="M20.4 8.6l-6.4 6.4-4-4-4.4 4.4"/><path d="M22 2L12 12"/></svg>`,
    'festival': `<svg ${svgAttrs}><path d="M4 22h16"/><path d="M12 2v20"/><path d="M8 6l4-4 4 4"/><path d="M6 10l6-4 6 4"/><path d="M4 14l8-4 8 4"/></svg>`,

    // Business & Karriere
    'business': `<svg ${svgAttrs}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>`,
    'networking': `<svg ${svgAttrs}><circle cx="12" cy="5" r="3"/><path d="M12 8v4"/><circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/><path d="M6 14v-1a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"/></svg>`,
    'konferenz': `${icon('users')}`,
    'seminar': `${icon('graduationCap')}`,
    'messe': `${icon('home')}`,

    // Kunst & Kultur
    'kunst': `${icon('sparkles')}`,
    'kultur': `<svg ${svgAttrs}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>`,
    'theater': `${icon('smile2')}`,
    'kino': `<svg ${svgAttrs}><rect x="2" y="2" width="20" height="20" rx="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>`,
    'ausstellung': `<svg ${svgAttrs}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>`,
    'museum': `${icon('home')}`,

    // Sport & Fitness
    'sport': `<svg ${svgAttrs}><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><path d="M2 12h20"/></svg>`,
    'fitness': `<svg ${svgAttrs}><path d="M6.5 6.5L17.5 17.5"/><path d="M4 8.5L8.5 4L12 7.5L7.5 12L4 8.5Z"/><path d="M12 16.5L16.5 12L20 15.5L15.5 20L12 16.5Z"/></svg>`,
    'laufen': `<svg ${svgAttrs}><circle cx="12" cy="5" r="2"/><path d="M4 17l4-4 4 4 4-4 4 4"/><path d="M12 7v6"/></svg>`,
    'yoga': `<svg ${svgAttrs}><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><path d="M8 11h8"/><path d="M10 15l2 6 2-6"/><path d="M6 11l-2 4"/><path d="M18 11l2 4"/></svg>`,

    // Food & Drinks
    'food': `${icon('coffee2')}`,
    'kulinarik': `${icon('coffee2')}`,
    'wine': `<svg ${svgAttrs}><path d="M8 22h8"/><path d="M12 11v11"/><path d="M5 3l2 10a4 4 0 0 0 4 4h2a4 4 0 0 0 4-4l2-10"/></svg>`,
    'kochen': `<svg ${svgAttrs}><path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"/><line x1="6" y1="17" x2="18" y2="17"/></svg>`,

    // Bildung & Workshop
    'workshop': `${icon('key')}`,
    'bildung': `${icon('graduationCap')}`,
    'vortrag': `<svg ${svgAttrs}><path d="M12 14l9-5-9-5-9 5 9 5z"/><path d="M12 14l6.16-3.422a12.083 12.083 0 0 1 .665 6.479A11.952 11.952 0 0 0 12 20.055a11.952 11.952 0 0 0-6.824-2.998 12.078 12.078 0 0 1 .665-6.479L12 14z"/></svg>`,
    'kurs': `<svg ${svgAttrs}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>`,

    // Outdoor & Nature
    'outdoor': `<svg ${svgAttrs}><path d="m8 3 4 8 5-5 5 15H2L8 3z"/></svg>`,
    'natur': `<svg ${svgAttrs}><path d="M12 22c4.97 0 9-2.24 9-5v-2c0-2.76-4.03-5-9-5s-9 2.24-9 5v2c0 2.76 4.03 5 9 5z"/><path d="M12 15c4.97 0 9-2.24 9-5V7c0-2.76-4.03-5-9-5S3 4.24 3 7v3c0 2.76 4.03 5 9 5z"/></svg>`,
    'wandern': `<svg ${svgAttrs}><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><path d="m9 16 3-4 3 4"/><path d="m6 22 3-6"/><path d="m18 22-3-6"/></svg>`,

    // Tech & Gaming
    'tech': `<svg ${svgAttrs}><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>`,
    'gaming': `<svg ${svgAttrs}><line x1="6" y1="12" x2="18" y2="12"/><line x1="12" y1="6" x2="12" y2="18"/><rect x="2" y="6" width="20" height="12" rx="2"/></svg>`,

    // Familie & Kinder
    'familie': `${icon('users')}`,
    'kinder': `<svg ${svgAttrs}><circle cx="12" cy="8" r="5"/><path d="M12 13v8"/><path d="M9 18h6"/></svg>`,

    // Wellness & Gesundheit
    'wellness': `${icon('heart')}`,
    'gesundheit': `${icon('heart')}`,

    // Sonstige
    'online': `${icon('globe')}`,
    'charity': `${icon('heart')}`,
    'mode': `<svg ${svgAttrs}><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>`,
    'comedy': `${icon('smile2')}`
  };

  // Find matching icon (try full match, then partial match)
  let iconMarkup = icons[name];
  if (!iconMarkup) {
    // Try partial match
    for (const [key, svg] of Object.entries(icons)) {
      if (name.includes(key) || key.includes(name)) {
        iconMarkup = svg;
        break;
      }
    }
  }

  // Default calendar icon if no match
  return iconMarkup || icon('calendar');
}

// ============================================================
// API SERVICE
// ============================================================

const EventsAPI = {
  cache: new Map(),
  cacheTTL: 60000, // 1 minute cache

  buildMethodUrl(methodName, args = {}) {
    const params = new URLSearchParams();
    Object.entries(args || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
        params.set(key, JSON.stringify(value));
      } else {
        params.set(key, String(value));
      }
    });
    const query = params.toString();
    return `/api/method/${methodName}${query ? `?${query}` : ''}`;
  },

  async fetchWithCache(url, args) {
    const cacheKey = JSON.stringify({ url, args });
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      return cached.data;
    }

    try {
      const endpoint = this.buildMethodUrl(url, args);
      const { normalized } = await Http.fetchJson(endpoint, { method: 'GET', cache: 'no-store' });
      if (!normalized?.success) {
        throw new Error(normalized?.toast || normalized?.code || 'Request failed');
      }

      const response = {
        success: true,
        code: normalized?.code || 'ok',
        toast: normalized?.toast || null,
        data: normalized?.data ?? {}
      };
      this.cache.set(cacheKey, { data: response, timestamp: Date.now() });
      return response;
    } catch (error) {
      console.error('API Error:', error);
      return {
        success: false,
        code: 'request_failed',
        toast: error?.message || 'Request failed',
        data: {}
      };
    }
  },

  async getCuratedSections(limit = 12) {
    return this.fetchWithCache(
      'cita_events.api.event.event_public_api.get_curated_sections',
      { limit }
    );
  },

  async getEventsFiltered(params) {
    try {
      const endpoint = this.buildMethodUrl(
        'cita_events.api.event.event_public_api.get_events_filtered',
        params
      );
      const { normalized } = await Http.fetchJson(endpoint, { method: 'GET', cache: 'no-store' });
      if (!normalized?.success) {
        throw new Error(normalized?.toast || normalized?.code || 'Request failed');
      }

      const data = normalized?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        return data;
      }

      return { events: [], has_more: false, total: 0 };
    } catch (error) {
      console.error('Error fetching events:', error);
      // User-Feedback bei Ladefehlern
      if (typeof frappe !== 'undefined' && frappe.show_alert) {
        window.showToast('Events konnten nicht geladen werden. Bitte versuche es erneut.', 'error');
      }
      return { events: [], has_more: false, total: 0, error: true };
    }
  },

  async toggleWatchlist(eventId, add = true) {
    if (!INITIAL_DATA.isLoggedIn) {
      window.location.href = `/login?redirect-to=${encodeURIComponent(window.location.pathname)}`;
      return { success: false };
    }

    try {
      const method = add
        ? 'cita_events.api.event.event_user_api.add_to_watchlist'
        : 'cita_events.api.event.event_user_api.remove_from_watchlist';

      const { normalized } = await Http.callMethod(method, { event: eventId });
      if (!normalized?.success) {
        throw new Error(normalized?.toast || normalized?.code || 'Request failed');
      }

      const data = normalized?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        return {
          ...data,
          success: true
        };
      }

      return {
        success: true,
        code: normalized?.code || 'success',
        toast: normalized?.toast || null,
        data: {},
      };
    } catch (error) {
      console.error('Error toggling watchlist:', error);
      // User-Feedback bei Watchlist-Fehlern
      if (typeof frappe !== 'undefined' && frappe.show_alert) {
        window.showToast('Merkliste konnte nicht aktualisiert werden.', 'warning');
      }
      return { success: false, error: true };
    }
  }
};

// ============================================================
// EVENT CARD RENDERER
// ============================================================

function renderEventCard(event, watchedEventIds = new Set(), compact = false) {
  const isWatched = watchedEventIds.has(event.name);
  const dateBadge = Formatters.formatDateBadge(event.start_date);
  const formattedDate = Formatters.formatDate(event.start_date);
  const imageUrl = event.banner_image || event.image || event.default_image || '/assets/cita_events/images/event/cita_default_messe.png';
  const isToday = isEventToday(event.start_date);
  const isSoon = isEventSoon(event.start_date);
  const multiDay = isMultiDay(event.start_date, event.end_date);
  const durationDays = multiDay ? getDurationDays(event.start_date, event.end_date) : 0;
  // Kapazitäts-Info (Backend sendet ui_remaining_allowed + available_tickets)
  const availableTickets = event.available_tickets !== undefined && event.available_tickets !== null
    ? parseInt(event.available_tickets, 10)
    : null;
  const hasCapacityInfo = event.ui_remaining_allowed && availableTickets !== null && !isNaN(availableTickets);
  const isSoldOut = hasCapacityInfo && availableTickets === 0;

  // Capacity badge mit Urgency-Stufen
  let capacityBadge = '';
  if (hasCapacityInfo) {
    if (availableTickets === 0) {
      capacityBadge = '<div class="event-card-capacity-badge event-card-capacity-badge--sold-out">Ausverkauft</div>';
    } else if (availableTickets <= 5) {
      capacityBadge = `<div class="event-card-capacity-badge event-card-capacity-badge--urgent">Letzte ${availableTickets} Plätze!</div>`;
    } else if (availableTickets <= 10) {
      capacityBadge = '<div class="event-card-capacity-badge event-card-capacity-badge--low">Wenige Plätze</div>';
    }
  }

  // Soon badge
  const soonBadge = isSoon && !isToday ? `
    <div class="event-card-soon-badge">
      ${icon('clock')}
      Bald
    </div>
  ` : '';

  // Multi-day badge
  const multidayBadge = multiDay ? `
    <div class="event-card-multiday-badge">
      ${icon('calendar')}
      ${durationDays} Tage
    </div>
  ` : '';

  // Online badge
  const onlineBadge = event.is_online ? `
    <div class="event-card-online-badge">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/>
        <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
      </svg>
      Online
    </div>
  ` : '';

  // Price display
  let priceHtml = '';
  if (event.is_free) {
    priceHtml = '<span class="event-card-price event-card-price--free">Kostenlos</span>';
  } else if (event.min_price) {
    const formattedPrice = Formatters.formatPriceHtml(event.min_price);
    priceHtml = `<span class="event-card-price event-card-price--paid"><span class="event-card-price-from">ab</span>${formattedPrice}</span>`;
  }

  // Highlight badge (replaces Featured)
  const highlightBadge = event.is_highlighted ? `
    <div class="event-card-highlight-badge">
      ${icon('starFilled')}
      Highlight
    </div>
  ` : '';

  // Card classes
  const cardClasses = [
    'event-card',
    event.is_highlighted ? 'event-card--highlighted' : '',
    isSoldOut ? 'event-card--sold-out' : ''
  ].filter(Boolean).join(' ');

  return `
    <article class="${cardClasses}" data-event-id="${event.name}">
      <a href="/event/${event.slug}" class="event-card-link">
        <div class="event-card-image">
          <img src="${imageUrl || '/assets/cita_events/images/event/cita_default_messe.png'}" alt="${escapeHtml(event.title)}" loading="lazy" onerror="this.onerror=null;this.src='/assets/cita_events/images/event/cita_default_messe.png';">

          <div class="event-card-date-badge${isToday ? ' event-card-date-badge--today' : ''}">
            <span class="event-card-date-day">${dateBadge.day}</span>
            <span class="event-card-date-month">${isToday ? 'HEUTE' : dateBadge.month}</span>
            ${dateBadge.time !== '00:00' ? `<span class="event-card-date-time">${dateBadge.time}</span>` : ''}
          </div>

          <button
            class="event-card-watchlist ${isWatched ? 'active' : ''}"
            data-event-id="${escapeHtml(event.name)}"
            aria-label="${isWatched ? 'Von Merkliste entfernen' : 'Zu Merkliste hinzufügen'}"
            title="${isWatched ? 'Von Merkliste entfernen' : 'Merken'}"
          >
            <svg viewBox="0 0 24 24" fill="${isWatched ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
            </svg>
          </button>

          ${capacityBadge}
          ${soonBadge}
          ${multidayBadge}
          ${onlineBadge}
          ${highlightBadge}
        </div>

        <div class="event-card-body">
          <h3 class="event-card-title">${escapeHtml(event.title)}</h3>
          ${event.organizer_name ? `<p class="event-card-organizer">${escapeHtml(event.organizer_name)}</p>` : ''}
          ${event.registration_count ? `
            <div class="event-card-social-proof">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
              </svg>
              <span>${event.registration_count} Teilnehmer</span>
            </div>
          ` : ''}

          <div class="event-card-meta">
            <div class="event-card-meta-item">
              ${icon('mapPin')}
              <span>${event.location ? escapeHtml(event.location) : 'Ort folgt'}</span>
            </div>
            <div class="event-card-meta-item">
              ${icon('calendar')}
              <span>${formattedDate}</span>
            </div>
          </div>

          ${!compact && event.teaser ? `<p class="event-card-teaser">${escapeHtml(Formatters.truncate(event.teaser, 120))}</p>` : ''}

          <div class="event-card-footer">
            <div class="event-card-footer-left">
              ${event.category ? `<span class="event-card-tag event-card-tag--category">${escapeHtml(event.category)}</span>` : ''}
            </div>
            <div class="event-card-footer-right">
              ${priceHtml}
            </div>
          </div>
        </div>
      </a>
    </article>
  `;
}

// Skeleton card for loading state
function renderSkeletonCard() {
  return `
    <div class="events-section-skeleton">
      <div class="events-section-skeleton-image"></div>
      <div class="events-section-skeleton-body">
        <div class="events-section-skeleton-title"></div>
        <div class="events-section-skeleton-meta"></div>
      </div>
    </div>
  `;
}

// ============================================================
// EVENT DISCOVERY APP
// ============================================================

class EventDiscoveryApp {
  constructor() {
    this.state = {
      sections: {
        recommended: [],
        trending: [],
        weekend: []
      },
      categories: [],
      locations: [],
      allEvents: [],
      filters: {
        search: '',
        category: '',
        location: '',
        date: '',
        price: '',
        sort: 'date'
      },
      page: 1,
      totalPages: 1,
      totalCount: 0,
      hasMore: true,
      isLoading: false,
      allEventsLoaded: false,
      watchedEventIds: new Set()
    };

    this.swipers = {};
    this.searchTimeout = null;
    this.infiniteScrollObserver = null;

    // Handler-Referenzen für Cleanup (Memory Leak Prevention)
    this._boundHandlers = {
      documentClick: null,
      windowScroll: null,
      windowScrollSpy: null,
      windowResize: null,
      containerScroll: null
    };

    this.init();
  }

  async init() {
    this._suppressBadge = true;
    this.parseUrlParams();
    this.bindEvents();
    this.createChipPortal(); // Mobile Filter Bottom Sheet
    await this.loadCuratedSections();
    this.initCarousels();
    this.initBackToTop();
    this.initTabNavigation();
    this.initScrollReveal();
    this.initFilterBadge();
    this.initShowAllLinks();
    this._suppressBadge = false;
    this.updateFilterBadge();

    // Deep-link with search/filter: land on results section so the user
    // immediately sees what matched their query (e.g. from hero search on /).
    const f = this.state.filters;
    const hasActiveFilter = f.search || f.category || f.location || f.date || f.price;
    if (hasActiveFilter) {
      const target = document.getElementById('section-all');
      if (target) {
        // Defer one frame so layout + sticky nav offsets are settled
        requestAnimationFrame(() => this.scrollToSection(target));
      }
    }
  }

  parseUrlParams() {
    const params = new URLSearchParams(window.location.search);

    // Whitelist für erlaubte Werte
    const VALID_DATES = ['today', 'week', 'weekend', 'month'];
    const VALID_SORTS = ['date', 'popularity', 'newest'];
    const VALID_PRICES = ['free', 'paid'];
    const MAX_TEXT_LENGTH = 100;

    // Sichere String-Extraktion mit Längenbegrenzung
    const safeString = (val, maxLen = MAX_TEXT_LENGTH) =>
      (val || '').substring(0, maxLen).trim();

    // Whitelist-Validierung
    const validateEnum = (val, allowed, defaultVal = '') =>
      allowed.includes(val) ? val : defaultVal;

    const rawDate = params.get('date');
    const rawSort = params.get('sort');
    const rawPrice = params.get('price');

    this.state.filters = {
      search: safeString(params.get('q')),
      category: safeString(params.get('cat'), 50),
      location: safeString(params.get('loc')),
      date: validateEnum(rawDate, VALID_DATES),
      price: validateEnum(rawPrice, VALID_PRICES),
      sort: validateEnum(rawSort, VALID_SORTS, 'date')
    };

    // Restore page from URL (validated: positive integer, capped at 500)
    const rawPage = parseInt(params.get('page'), 10);
    this.state.page = (rawPage > 0 && rawPage <= 500) ? rawPage : 1;

    // Set search input value
    const searchInput = document.getElementById('events-search-input');
    if (searchInput && this.state.filters.search) {
      searchInput.value = this.state.filters.search;
    }
  }

  updateUrl() {
    const params = new URLSearchParams();
    const f = this.state.filters;

    if (f.search) params.set('q', f.search);
    if (f.category) params.set('cat', f.category);
    if (f.location) params.set('loc', f.location);
    if (f.date) params.set('date', f.date);
    if (f.price) params.set('price', f.price);
    if (f.sort && f.sort !== 'date') params.set('sort', f.sort);
    if (this.state.page > 1) params.set('page', this.state.page);

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
  }

  bindEvents() {
    // Search input
    const searchInput = document.getElementById('events-search-input');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(async () => {
          const val = e.target.value.trim();
          // Min 2 Zeichen für Suche, leerer String setzt Filter zurück
          if (val.length > 0 && val.length < 2) return;
          this.state.filters.search = val;
          this.updateUrl();
          await this.loadAllEvents(true);
          // Bring results into view — user just searched, they want to see hits
          if (val) this.scrollToSection(document.getElementById('section-all'));
        }, 300);
      });

      // Enter key — immediate search + scroll (skips debounce)
      searchInput.addEventListener('keydown', async (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        clearTimeout(this.searchTimeout);
        const val = searchInput.value.trim();
        if (val.length > 0 && val.length < 2) return;
        this.state.filters.search = val;
        this.updateUrl();
        await this.loadAllEvents(true);
        this.scrollToSection(document.getElementById('section-all'));
      });
    }

    // Search clear button
    const searchClear = document.getElementById('events-search-clear');
    if (searchClear) {
      searchClear.addEventListener('click', () => {
        if (searchInput) searchInput.value = '';
        const mobileInput = document.getElementById('events-mobile-search-input');
        if (mobileInput) mobileInput.value = '';
        this.state.filters.search = '';
        this.updateUrl();
        this.loadAllEvents(true);
      });
    }

    // Mobile sticky search — forwards to the hero input so the existing
    // debounced handler stays the single source of truth for search semantics.
    const mobileSearchInput = document.getElementById('events-mobile-search-input');
    if (mobileSearchInput && searchInput) {
      // Seed mobile input with any URL-hydrated or current hero value
      if (searchInput.value) mobileSearchInput.value = searchInput.value;

      // Mobile → Hero: mirror value, re-dispatch so the debounced/enter
      // listeners above run unchanged
      mobileSearchInput.addEventListener('input', () => {
        if (searchInput.value !== mobileSearchInput.value) {
          searchInput.value = mobileSearchInput.value;
          searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
      mobileSearchInput.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (searchInput.value !== mobileSearchInput.value) {
          searchInput.value = mobileSearchInput.value;
        }
        searchInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }));
      });

      // Hero → Mobile: keep mobile in sync when clear-button or other paths
      // write to the hero input
      searchInput.addEventListener('input', () => {
        if (mobileSearchInput.value !== searchInput.value) {
          mobileSearchInput.value = searchInput.value;
        }
      });
    }

    // Smart chips
    document.querySelectorAll('.events-chip').forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleChipDropdown(chip);
      });
    });

    // Chip dropdown items
    document.querySelectorAll('.events-chip-dropdown-item').forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const chip = item.closest('.events-chip');
        const filterType = chip?.dataset.filter;
        const value = item.dataset.value;

        if (filterType) {
          this.applyFilter(filterType, value);
          this.closeAllChipDropdowns();
        }
      });
    });

    // View toggle (if present)
    document.querySelectorAll('.events-view-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.events-view-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        const view = btn.dataset.view;
        const grid = document.getElementById('events-grid');
        if (grid) {
          grid.classList.toggle('events-grid--list', view === 'list');
        }
      });
    });

    // Clear filters button (in empty state)
    const clearFiltersBtn = document.getElementById('events-empty-reset');
    if (clearFiltersBtn) {
      clearFiltersBtn.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }

    // Sort dropdown
    const sortBtn = document.getElementById('events-sort-btn');
    const sortDropdown = sortBtn?.closest('.events-sort-dropdown');
    const sortMenu = document.getElementById('events-sort-menu');

    if (sortBtn && sortDropdown) {
      sortBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = sortDropdown.classList.contains('open');
        this.closeAllDropdowns();
        if (!isOpen) {
          sortDropdown.classList.add('open');
        }
      });

      // Sort items
      sortMenu.querySelectorAll('.events-sort-item').forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const sortValue = item.dataset.sort;
          if (sortValue) {
            this.syncSortUI(sortValue);
            this.state.filters.sort = sortValue;
            this.updateUrl();
            this.loadAllEvents(true);
          }
          this.closeAllDropdowns();
        });
      });
    }

    // Close dropdowns on outside click (mit Referenz für Cleanup)
    this._boundHandlers.documentClick = () => this.closeAllDropdowns();
    document.addEventListener('click', this._boundHandlers.documentClick);
  }

  closeAllDropdowns() {
    // Close chip dropdowns
    this.closeAllChipDropdowns();
    // Close sort dropdown
    const sortDropdown = document.querySelector('.events-sort-dropdown');
    if (sortDropdown) {
      sortDropdown.classList.remove('open');
    }
  }

  toggleChipDropdown(chip) {
    // Mobile: Portal verwenden
    if (window.innerWidth <= 640) {
      // Portal on-demand erstellen falls nicht vorhanden
      if (!this.chipPortal) {
        this.createChipPortal();
      }

      if (this.activeChip === chip) {
        this.closeChipPortal();
      } else {
        this.closeChipPortal();
        this.openChipPortal(chip);
      }
      return;
    }

    // Desktop: Bestehendes Verhalten
    const wasOpen = chip.classList.contains('open');
    this.closeAllChipDropdowns();
    if (!wasOpen) {
      chip.classList.add('open');
    }
  }

  closeAllChipDropdowns() {
    document.querySelectorAll('.events-chip.open').forEach(c => {
      c.classList.remove('open');
    });
  }

  // ============================================================
  // MOBILE CHIP PORTAL (Bottom Sheet)
  // ============================================================

  createChipPortal() {
    // Portal nur einmal erstellen - wiederverenden falls vorhanden
    const existingPortal = document.querySelector('.events-chip-portal');
    if (existingPortal) {
      this.chipPortal = existingPortal;
      return;
    }

    const portal = document.createElement('div');
    portal.className = 'events-chip-portal';
    portal.innerHTML = `
      <div class="events-chip-portal-backdrop" data-portal-close></div>
      <div class="events-chip-portal-content">
        <div class="events-chip-portal-header">
          <span class="events-chip-portal-title">Filter auswählen</span>
          <button type="button" class="events-chip-portal-close" data-portal-close aria-label="Schließen">
            ${icon('xMark')}
          </button>
        </div>
        <div class="events-chip-portal-items"></div>
      </div>
    `;
    document.body.appendChild(portal);

    // Close-Handler
    portal.querySelectorAll('[data-portal-close]').forEach(el => {
      el.addEventListener('click', () => this.closeChipPortal());
    });

    // ESC-Taste schließt Portal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.chipPortal?.classList.contains('active')) {
        this.closeChipPortal();
      }
    });

    this.chipPortal = portal;
  }

  openChipPortal(chip) {
    if (!this.chipPortal) return;

    const dropdown = chip.querySelector('.events-chip-dropdown');
    if (!dropdown) return;

    const filterType = chip.dataset.filter;
    const chipLabel = chip.querySelector('.events-chip-label')?.textContent || 'Filter';

    // Titel setzen
    const title = this.chipPortal.querySelector('.events-chip-portal-title');
    if (title) title.textContent = chipLabel;

    // Items kopieren
    const itemsContainer = this.chipPortal.querySelector('.events-chip-portal-items');
    if (itemsContainer) {
      itemsContainer.innerHTML = dropdown.innerHTML;

      // Event-Listener für Items hinzufügen
      itemsContainer.querySelectorAll('.events-chip-dropdown-item').forEach(item => {
        item.addEventListener('click', () => {
          const value = item.dataset.value;
          if (filterType) {
            this.applyFilter(filterType, value);
            this.closeChipPortal();
          }
        });
      });
    }

    // Scroll-Position speichern und Body sperren
    this._scrollY = window.scrollY;
    document.body.style.top = `-${this._scrollY}px`;
    document.body.classList.add('chip-dropdown-open');

    // Portal öffnen
    this.chipPortal.classList.add('active');
    this.activeChip = chip;
  }

  closeChipPortal() {
    if (!this.chipPortal) return;

    this.chipPortal.classList.remove('active');

    // Body-Scroll wiederherstellen
    document.body.classList.remove('chip-dropdown-open');
    document.body.style.top = '';
    if (this._scrollY !== undefined) {
      window.scrollTo(0, this._scrollY);
    }

    this.activeChip = null;
  }

  applyFilter(type, value) {
    const chip = document.querySelector(`.events-chip[data-filter="${type}"]`);

    // Toggle filter if same value
    if (this.state.filters[type] === value) {
      this.state.filters[type] = '';
      if (chip) {
        chip.classList.remove('active');
        const valueSpan = chip.querySelector('.events-chip-value');
        if (valueSpan) valueSpan.remove();
      }
    } else {
      this.state.filters[type] = value;
      if (chip) {
        chip.classList.add('active');
        // Show value in chip
        let valueSpan = chip.querySelector('.events-chip-value');
        if (!valueSpan) {
          valueSpan = document.createElement('span');
          valueSpan.className = 'events-chip-value';
          const chevron = chip.querySelector('.events-chip-chevron');
          if (chevron) {
            chip.insertBefore(valueSpan, chevron);
          } else {
            chip.appendChild(valueSpan);
          }
        }
        // Find label from dropdown (CSS.escape für sichere Attribut-Selektion)
        const safeValue = CSS.escape(value);
        const item = chip.querySelector(`.events-chip-dropdown-item[data-value="${safeValue}"]`);
        valueSpan.textContent = item?.dataset.label || value;
      }
    }

    // Update dropdown active states
    if (chip) {
      chip.querySelectorAll('.events-chip-dropdown-item').forEach(item => {
        item.classList.toggle('active', item.dataset.value === this.state.filters[type]);
      });
    }

    this.updateUrl();
    this.updateActiveFiltersBar();
    this.updateFilterBadge();
    return this.loadAllEvents(true);
  }

  updateActiveFiltersBar() {
    const bar = document.getElementById('events-active-filters');
    if (!bar) return;

    const filters = [];
    const f = this.state.filters;

    const filterLabels = {
      category: 'Kategorie',
      location: 'Ort',
      date: { today: 'Heute', week: 'Diese Woche', month: 'Dieser Monat' },
      price: { free: 'Kostenlos', paid: 'Kostenpflichtig' }
    };

    if (f.search) {
      filters.push({ type: 'search', label: `"${f.search}"` });
    }
    if (f.category) {
      filters.push({ type: 'category', label: f.category });
    }
    if (f.location) {
      filters.push({ type: 'location', label: f.location });
    }
    if (f.date) {
      filters.push({ type: 'date', label: filterLabels.date[f.date] || f.date });
    }
    if (f.price) {
      filters.push({ type: 'price', label: filterLabels.price[f.price] || f.price });
    }

    if (filters.length === 0) {
      bar.classList.add('hidden');
      bar.innerHTML = '';
      return;
    }

    bar.classList.remove('hidden');
    bar.innerHTML = `
      ${filters.map(f => `
        <span class="events-active-filter-tag">
          ${escapeHtml(f.label)}
          <button data-type="${f.type}" title="Filter entfernen">
            ${icon('xMark')}
          </button>
        </span>
      `).join('')}
      <button class="events-clear-all-btn">Alle löschen</button>
    `;

    // Bind remove events
    bar.querySelectorAll('.events-active-filter-tag button').forEach(btn => {
      btn.addEventListener('click', () => {
        const type = btn.dataset.type;
        this.applyFilter(type, '');
      });
    });

    const clearAllBtn = bar.querySelector('.events-clear-all-btn');
    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }
  }

  clearAllFilters() {
    this.state.filters = {
      search: '',
      category: '',
      location: '',
      date: '',
      price: '',
      sort: 'date'
    };

    // Clear search input (both hero + mobile sticky)
    const searchInput = document.getElementById('events-search-input');
    if (searchInput) searchInput.value = '';
    const mobileSearchInput = document.getElementById('events-mobile-search-input');
    if (mobileSearchInput) mobileSearchInput.value = '';

    // Clear chip states
    document.querySelectorAll('.events-chip').forEach(chip => {
      chip.classList.remove('active');
      const valueSpan = chip.querySelector('.events-chip-value');
      if (valueSpan) valueSpan.remove();
      chip.querySelectorAll('.events-chip-dropdown-item').forEach(item => {
        item.classList.remove('active');
      });
    });

    this.updateUrl();
    this.updateActiveFiltersBar();
    this.updateFilterBadge();
    this.loadAllEvents(true);
  }

  // ============================================================
  // DATA LOADING
  // ============================================================

  async loadCuratedSections() {
    // Show loading skeletons
    ['highlights', 'recommended', 'trending', 'weekend'].forEach(section => {
      const container = document.querySelector(`[data-section="${section}"] .swiper-wrapper`);
      if (container) {
        container.innerHTML = Array(4).fill(renderSkeletonCard()).join('');
      }
    });

    const categoriesGrid = document.getElementById('events-category-grid');
    if (categoriesGrid) {
      categoriesGrid.innerHTML = Array(6).fill(`
        <div class="events-category-skeleton">
          <div class="events-category-skeleton-icon"></div>
          <div class="events-category-skeleton-text"></div>
        </div>
      `).join('');
    }

    // Load data
    const response = await EventsAPI.getCuratedSections(12);
    const data = response?.data;

    if (!response?.success || !data || typeof data !== 'object' || Array.isArray(data)) {
      console.error('Failed to load curated sections');
      // User-Feedback bei Ladefehler der Highlight-Sektionen
      if (typeof frappe !== 'undefined' && frappe.show_alert) {
        window.showToast('Empfohlene Events konnten nicht geladen werden.', 'warning');
      }
      // Skeleton entfernen, leeren Zustand anzeigen
      ['highlights', 'recommended', 'trending', 'weekend'].forEach(section => {
        const container = document.querySelector(`[data-section="${section}"] .swiper-wrapper`);
        if (container) container.innerHTML = '';
      });
      // Clear spotlight skeleton
      const spotlight = document.getElementById('events-spotlight');
      if (spotlight) spotlight.innerHTML = '';
      // Fallback: Load "Alle Events" even when curated sections fail
      await this.loadAllEvents(true);
      return;
    }

    // Update state
    this.state.sections.highlights = data.highlights || [];
    this.state.sections.recommended = data.recommended || [];
    this.state.sections.trending = data.trending || [];
    this.state.sections.weekend = data.weekend || [];
    this.state.categories = data.categories || [];
    this.state.locations = data.filter_options?.locations || [];

    // Render sections
    this.renderHighlightsSection(this.state.sections.highlights);
    this.renderSection('recommended', this.state.sections.recommended);
    this.renderSection('trending', this.state.sections.trending);
    this.renderSection('weekend', this.state.sections.weekend);
    this.renderCategories(this.state.categories);
    this.populateChipDropdowns();

    // Track highlight impressions
    if (this.state.sections.highlights.length > 0) {
      this.trackHighlightImpressions(this.state.sections.highlights);
    }

    // Update stats banner with category + location data
    this.updateStatsBanner();

    // Load initial all events
    await this.loadAllEvents(true);
  }

  renderSection(name, events) {
    const section = document.querySelector(`[data-section="${name}"]`);
    if (!section) return;

    const container = section.querySelector('.swiper-wrapper');
    if (!container) return;

    if (events.length === 0) {
      section.classList.add('hidden');
      return;
    }

    section.classList.remove('hidden');
    // Crossfade: fade out before replacing content
    section.classList.add('is-loading');
    container.innerHTML = events.map(event => `
      <div class="swiper-slide">
        ${renderEventCard(event, this.state.watchedEventIds, true)}
      </div>
    `).join('');
    requestAnimationFrame(() => section.classList.remove('is-loading'));
  }

  renderHighlightsSection(events) {
    const section = document.querySelector('[data-section="highlights"]');
    if (!section) return;

    const spotlightContainer = document.getElementById('events-spotlight');
    const carouselWrapper = section.querySelector('.swiper-wrapper');
    const carousel = section.querySelector('.events-carousel');

    if (!events || events.length === 0) {
      section.classList.add('hidden');
      return;
    }

    section.classList.remove('hidden');

    // First event = hero spotlight
    const spotlightEvent = events[0];
    const remainingEvents = events.slice(1);
    const formattedDate = Formatters.formatDate(spotlightEvent.start_date);
    const imageUrl = spotlightEvent.banner_image || spotlightEvent.image || spotlightEvent.default_image || '/assets/cita_events/images/event/cita_default_messe.png';

    // Price display for spotlight
    let priceHtml = '';
    if (spotlightEvent.is_free) {
      priceHtml = '<span class="events-spotlight-price events-spotlight-price--free">Kostenlos</span>';
    } else if (spotlightEvent.min_price) {
      const formattedPrice = Formatters.formatPriceHtml(spotlightEvent.min_price);
      priceHtml = `<span class="events-spotlight-price events-spotlight-price--paid"><span class="events-spotlight-price-from">ab</span>${formattedPrice}</span>`;
    }

    if (spotlightContainer) {
      spotlightContainer.innerHTML = `
        <a href="/event/${spotlightEvent.slug}" class="events-spotlight-card">
          <div class="events-spotlight-image">
            <img src="${imageUrl}" alt="${escapeHtml(spotlightEvent.title)}" loading="eager"
                 onerror="this.onerror=null;this.src='/assets/cita_events/images/event/cita_default_messe.png';">
          </div>
          <div class="events-spotlight-body">
            <div class="events-spotlight-badge">
              ${icon('starFilled')}
              Highlight
            </div>
            <h3 class="events-spotlight-title">${escapeHtml(spotlightEvent.title)}</h3>
            <div class="events-spotlight-meta">
              <div class="events-spotlight-meta-item">
                ${icon('calendar')}
                <span>${formattedDate}</span>
              </div>
              ${spotlightEvent.location ? `
              <div class="events-spotlight-meta-item">
                ${icon('mapPin')}
                <span>${escapeHtml(spotlightEvent.location)}</span>
              </div>
              ` : ''}
            </div>
            ${spotlightEvent.teaser ? `<p class="events-spotlight-teaser">${escapeHtml(Formatters.truncate(spotlightEvent.teaser, 200))}</p>` : ''}
            <div class="events-spotlight-footer">
              <div class="events-spotlight-cta">
                Mehr erfahren
                ${icon('arrowRight')}
              </div>
              ${priceHtml}
            </div>
          </div>
        </a>
      `;
    }

    // Remaining events in compact carousel
    if (carouselWrapper) {
      if (remainingEvents.length === 0) {
        if (carousel) carousel.classList.add('hidden');
      } else {
        if (carousel) carousel.classList.remove('hidden');
        carouselWrapper.innerHTML = remainingEvents.map(event => `
          <div class="swiper-slide">
            ${renderEventCard(event, this.state.watchedEventIds, true)}
          </div>
        `).join('');
      }
    }
  }

  renderCategories(categories) {
    const grid = document.getElementById('events-category-grid');
    if (!grid) return;

    if (categories.length === 0) {
      grid.closest('.events-section')?.classList.add('hidden');
      return;
    }

    grid.innerHTML = categories.map(cat => {
      const catName = cat.label || cat.value || cat.name || '';
      return `
      <a href="/event?cat=${encodeURIComponent(cat.value || catName)}" class="events-category-card" data-category="${escapeHtml(cat.value || catName)}">
        <span class="events-category-icon">${getCategoryIcon(catName)}</span>
        <span class="events-category-name">${escapeHtml(catName)}</span>
        <span class="events-category-count">${cat.count} ${cat.count === 1 ? 'Event' : 'Events'}</span>
      </a>
    `}).join('');

    // Bind click events for SPA-like navigation
    grid.querySelectorAll('.events-category-card').forEach(card => {
      card.addEventListener('click', async (e) => {
        e.preventDefault();
        const category = card.dataset.category;
        await this.applyFilter('category', category);
        this.scrollToSection(document.getElementById('section-all'));
      });
    });
  }

  populateChipDropdowns() {
    // Category Chip Dropdown befüllen
    // API liefert: { value: "Musik", label: "Musik", count: 5 }
    const categoryDropdown = document.querySelector('.events-chip-dropdown[data-dropdown="category"]');
    if (categoryDropdown && this.state.categories.length > 0) {
      categoryDropdown.innerHTML = `
        <div class="events-chip-dropdown-item" data-value="" data-label="Alle">
          <span class="events-chip-dropdown-icon">
            ${icon('grid3x3')}
          </span>
          <span class="events-chip-dropdown-label">Alle Kategorien</span>
        </div>
        ${this.state.categories.map(cat => {
          const catName = cat.label || cat.value || cat.name || '';
          return `
          <div class="events-chip-dropdown-item" data-value="${escapeHtml(cat.value || catName)}" data-label="${escapeHtml(catName)}">
            <span class="events-chip-dropdown-icon">${getCategoryIcon(catName)}</span>
            <span class="events-chip-dropdown-label">${escapeHtml(catName)}</span>
            <span class="events-chip-dropdown-count">${cat.count}</span>
          </div>
        `}).join('')}
      `;

      // Event-Listener für dynamisch erstellte Items binden
      categoryDropdown.querySelectorAll('.events-chip-dropdown-item').forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const value = item.dataset.value;
          this.applyFilter('category', value);
          this.closeAllChipDropdowns();
        });
      });
    }

    // Location Chip Dropdown befüllen
    // API liefert: { value: "Berlin", label: "Berlin", count: 3 }
    const locationDropdown = document.querySelector('.events-chip-dropdown[data-dropdown="location"]');
    if (locationDropdown && this.state.locations.length > 0) {
      locationDropdown.innerHTML = `
        <div class="events-chip-dropdown-item" data-value="" data-label="Alle">
          <span class="events-chip-dropdown-icon">
            ${icon('globe')}
          </span>
          <span class="events-chip-dropdown-label">Alle Orte</span>
        </div>
        ${this.state.locations.map(loc => {
          const locName = loc.label || loc.value || '';
          return `
          <div class="events-chip-dropdown-item" data-value="${escapeHtml(loc.value || locName)}" data-label="${escapeHtml(locName)}">
            <span class="events-chip-dropdown-icon">
              ${icon('mapPin')}
            </span>
            <span class="events-chip-dropdown-label">${escapeHtml(locName)}</span>
            <span class="events-chip-dropdown-count">${loc.count}</span>
          </div>
        `}).join('')}
      `;

      // Event-Listener für dynamisch erstellte Items binden
      locationDropdown.querySelectorAll('.events-chip-dropdown-item').forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const value = item.dataset.value;
          this.applyFilter('location', value);
          this.closeAllChipDropdowns();
        });
      });
    }
  }

  async loadAllEvents(reset = false) {
    if (this.state.isLoading) return;

    this.state.isLoading = true;

    if (reset) {
      this.state.page = 1;
      this.updateUrl();
    }

    const grid = document.getElementById('events-grid');
    const loadingEl = document.getElementById('events-loading-more');
    const emptyEl = document.getElementById('events-empty-state');
    const paginationEl = document.getElementById('events-pagination');

    // Accessibility: aria-busy during load (also disables pagination clicks via CSS)
    if (grid) {
      grid.setAttribute('aria-busy', 'true');
      // Show skeleton
      grid.innerHTML = Array(6).fill(`
        <div class="events-skeleton-card">
          <div class="events-skeleton-image"></div>
          <div class="events-skeleton-body">
            <div class="events-skeleton-title"></div>
            <div class="events-skeleton-meta">
              <div class="events-skeleton-meta-line"></div>
              <div class="events-skeleton-meta-line"></div>
            </div>
          </div>
        </div>
      `).join('');
    }

    if (loadingEl) loadingEl.classList.remove('hidden');
    if (paginationEl) paginationEl.setAttribute('aria-busy', 'true');

    const f = this.state.filters;
    const result = await EventsAPI.getEventsFiltered({
      page: this.state.page,
      limit: 20,
      search: f.search || null,
      category: f.category || null,
      location: f.location || null,
      date_preset: f.date || null,
      price: f.price || null,
      sort: f.sort || 'date'
    });

    this.state.isLoading = false;

    if (grid) grid.setAttribute('aria-busy', 'false');
    if (paginationEl) paginationEl.removeAttribute('aria-busy');
    if (loadingEl) loadingEl.classList.add('hidden');

    const events = result.events || [];
    this.state.allEvents = events;
    this.state.hasMore = result.has_more || false;
    this.state.totalPages = result.total_pages || 1;
    this.state.totalCount = result.total || result.total_count || 0;

    // Render
    if (grid) {
      if (events.length === 0) {
        grid.innerHTML = '';
        grid.classList.add('hidden');
        if (emptyEl) emptyEl.classList.remove('hidden');
      } else {
        if (emptyEl) emptyEl.classList.add('hidden');
        grid.classList.remove('hidden');
        grid.classList.add('is-loading');
        grid.innerHTML = events.map(e =>
          renderEventCard(e, this.state.watchedEventIds)
        ).join('');
        requestAnimationFrame(() => grid.classList.remove('is-loading'));
      }
    }

    // Render pagination
    this.renderPagination();

    // Update result count
    const total = this.state.totalCount;
    const countEl = document.getElementById('events-results-count');
    if (countEl) {
      countEl.textContent = `${total} Event${total !== 1 ? 's' : ''}`;
    }

    // Update stats banner event count on first load
    if (reset) {
      this.updateStatsBanner(total);
    }

    this.state.allEventsLoaded = true;
  }

  // ============================================================
  // PAGINATION
  // ============================================================

  goToPage(pageNum) {
    if (this.state.isLoading) return;
    if (pageNum < 1 || pageNum > this.state.totalPages || pageNum === this.state.page) return;
    this.state.page = pageNum;
    this.updateUrl();
    this.loadAllEvents(false);
    // Scroll to top of "Alle Events" section
    const section = document.getElementById('section-all');
    if (section) {
      const navHeight = document.getElementById('events-tabs-nav')?.offsetHeight || 0;
      const targetPos = section.offsetTop - navHeight - 16;
      window.scrollTo({ top: targetPos, behavior: 'smooth' });
    }
  }

  renderPagination() {
    const container = document.getElementById('events-pagination');
    if (!container) return;

    const { page, totalPages, totalCount } = this.state;

    // Hide pagination if only one page or no results
    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    // Build page numbers with ellipsis
    const pageNumbers = this._buildPageNumbers(page, totalPages);

    const prevDisabled = page <= 1 ? 'disabled' : '';
    const nextDisabled = page >= totalPages ? 'disabled' : '';

    container.innerHTML = `
      <div class="events-pagination-info">
        Seite ${page} von ${totalPages} (${totalCount} Events)
      </div>
      <div class="events-pagination-controls">
        <button class="events-pagination-btn events-pagination-prev" ${prevDisabled} aria-label="Vorherige Seite">
          ${icon('chevronLeft')}
        </button>
        ${pageNumbers.map(p => {
          if (p === '...') {
            return '<span class="events-pagination-ellipsis">&hellip;</span>';
          }
          const isActive = p === page ? ' events-pagination-page--active' : '';
          return `<button class="events-pagination-page${isActive}" data-page="${p}" aria-label="Seite ${p}"${p === page ? ' aria-current="page"' : ''}>${p}</button>`;
        }).join('')}
        <button class="events-pagination-btn events-pagination-next" ${nextDisabled} aria-label="Nächste Seite">
          ${icon('chevronRight')}
        </button>
      </div>
    `;

    // Bind click handlers
    container.querySelector('.events-pagination-prev')?.addEventListener('click', () => {
      this.goToPage(page - 1);
    });
    container.querySelector('.events-pagination-next')?.addEventListener('click', () => {
      this.goToPage(page + 1);
    });
    container.querySelectorAll('.events-pagination-page[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.goToPage(parseInt(btn.dataset.page, 10));
      });
    });
  }

  _buildPageNumbers(current, total) {
    // Always show: first, last, current, and neighbors
    // Use ellipsis for gaps
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages = new Set([1, total]);

    // Current and neighbors
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.add(i);
    }

    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];

    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
        result.push('...');
      }
      result.push(sorted[i]);
    }

    return result;
  }

  // ============================================================
  // CAROUSELS (Swiper)
  // ============================================================

  initCarousels() {
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper not loaded');
      return;
    }

    ['highlights', 'recommended', 'trending', 'weekend'].forEach(name => {
      const container = document.querySelector(`[data-section="${name}"] .swiper`);
      if (!container) return;

      this.swipers[name] = new Swiper(container, {
        slidesPerView: 1.5,
        spaceBetween: 12,
        watchSlidesProgress: true,
        navigation: {
          prevEl: container.querySelector('.swiper-button-prev'),
          nextEl: container.querySelector('.swiper-button-next')
        },
        pagination: {
          el: container.querySelector('.swiper-pagination'),
          clickable: true,
          renderBullet: function (index, className) {
            return `<span class="events-dot ${className}"></span>`;
          }
        },
        breakpoints: {
          480: { slidesPerView: 1.8, spaceBetween: 14 },
          640: { slidesPerView: 2.2, spaceBetween: 20 },
          1024: { slidesPerView: 3, spaceBetween: 24 },
          1280: { slidesPerView: 4, spaceBetween: 24 }
        }
      });
    });
  }

  // ============================================================
  // HIGHLIGHT ANALYTICS TRACKING
  // ============================================================

  async trackHighlightImpressions(events) {
    if (!events || events.length === 0) return;

    try {
      await Http.callMethod('cita_events.api.event.highlight_analytics.track_highlight_impression', {
        events: events.map(e => e.name),
        section: 'highlights'
      });
    } catch (error) {
      console.warn('Failed to track highlight impressions:', error);
    }
  }

  // Infinite scroll removed — replaced by page-based pagination (see renderPagination)

  // ============================================================
  // BACK TO TOP
  // ============================================================

  initBackToTop() {
    const btn = document.createElement('button');
    btn.className = 'events-back-to-top';
    btn.setAttribute('aria-label', 'Nach oben');
    btn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="18 15 12 9 6 15"/>
      </svg>
    `;
    document.body.appendChild(btn);

    // Handler mit Referenz für Cleanup
    let ticking = false;
    this._boundHandlers.windowScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          btn.classList.toggle('visible', window.scrollY > 400);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', this._boundHandlers.windowScroll, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ============================================================
  // TAB NAVIGATION
  // ============================================================

  initTabNavigation() {
    const nav = document.getElementById('events-tabs-nav');
    const container = nav?.querySelector('.events-tabs-container');
    const tabs = nav?.querySelectorAll('.events-tab');

    if (!nav || !tabs || tabs.length === 0) return;

    // Store section elements for scroll spy
    const sections = [];
    tabs.forEach(tab => {
      const targetId = tab.dataset.target;
      const section = document.getElementById(targetId);
      if (section) {
        sections.push({ id: targetId, element: section, tab });
      }
    });

    // Tab click handler
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const targetId = tab.dataset.target;
        const targetSection = document.getElementById(targetId);

        if (targetSection) {
          // Update active state immediately
          this.setActiveTab(tab, tabs);
          this.scrollToSection(targetSection);

          // Lazy-load "Alle Events" on tab click if not yet loaded or previous load returned empty
          if (targetId === 'section-all') {
            const grid = document.getElementById('events-grid');
            const gridEmpty = grid && grid.classList.contains('hidden') && this.state.allEvents.length === 0;
            if (!this.state.isLoading && (!this.state.allEventsLoaded || gridEmpty)) {
              this.state.allEventsLoaded = false;
              this.loadAllEvents(true);
            }
            // Ensure scroll-reveal does not keep section invisible
            if (targetSection && !targetSection.classList.contains('revealed')) {
              targetSection.classList.add('revealed');
            }
          }
        }
      });
    });

    // Scroll spy - update active tab based on scroll position (mit Referenz für Cleanup)
    let scrollSpyTicking = false;
    this._boundHandlers.windowScrollSpy = () => {
      if (!scrollSpyTicking) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY + nav.offsetHeight + 100;

          // Find the section that's currently in view
          let activeSection = sections[0];
          for (const section of sections) {
            if (section.element.offsetTop <= scrollPosition) {
              activeSection = section;
            }
          }

          if (activeSection) {
            this.setActiveTab(activeSection.tab, tabs);
          }

          scrollSpyTicking = false;
        });
        scrollSpyTicking = true;
      }
    };

    window.addEventListener('scroll', this._boundHandlers.windowScrollSpy, { passive: true });

    // Scroll indicators for horizontal scroll on mobile (mit Referenzen für Cleanup)
    if (container) {
      this._boundHandlers.containerScroll = () => {
        const canScrollLeft = container.scrollLeft > 5;
        const canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth - 5;

        nav.classList.toggle('can-scroll-left', canScrollLeft);
        nav.classList.toggle('can-scroll-right', canScrollRight);
      };
      this._boundHandlers.windowResize = this._boundHandlers.containerScroll;
      this._tabContainer = container; // Referenz für Cleanup speichern

      container.addEventListener('scroll', this._boundHandlers.containerScroll, { passive: true });
      window.addEventListener('resize', this._boundHandlers.windowResize, { passive: true });

      // Initial check
      setTimeout(this._boundHandlers.containerScroll, 100);
    }

    // Scroll active tab into view
    this.scrollTabIntoView = (activeTab) => {
      if (container && activeTab) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();

        if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
          activeTab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
    };
  }

  setActiveTab(activeTab, allTabs) {
    allTabs.forEach(tab => {
      const isActive = tab === activeTab;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Scroll active tab into view on mobile
    if (this.scrollTabIntoView) {
      this.scrollTabIntoView(activeTab);
    }
  }

  // ============================================================
  // SCROLL-REVEAL ANIMATIONS
  // ============================================================

  initScrollReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.scrollRevealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          this.scrollRevealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });

    document.querySelectorAll('.scroll-reveal').forEach(el => {
      this.scrollRevealObserver.observe(el);
    });
  }

  // ============================================================
  // STATS BANNER
  // ============================================================

  updateStatsBanner(totalEvents) {
    const eventsEl = document.getElementById('stat-events');
    const citiesEl = document.getElementById('stat-cities');
    const categoriesEl = document.getElementById('stat-categories');

    if (totalEvents !== undefined && totalEvents !== null && eventsEl) {
      eventsEl.textContent = totalEvents.toLocaleString('de-DE');
    }

    if (citiesEl && this.state.locations) {
      citiesEl.textContent = this.state.locations.length.toLocaleString('de-DE');
    }

    if (categoriesEl && this.state.categories) {
      categoriesEl.textContent = this.state.categories.length.toLocaleString('de-DE');
    }
  }

  // ============================================================
  // ACTIVE-FILTER FLOATING BADGE
  // ============================================================

  initFilterBadge() {
    const clearBtn = document.getElementById('events-filter-badge-clear');
    if (clearBtn) {
      this._filterBadgeClearHandler = () => this.clearAllFilters();
      clearBtn.addEventListener('click', this._filterBadgeClearHandler);
    }

  }

  syncSortUI(sortValue) {
    const sortMenu = document.getElementById('events-sort-menu');
    const sortBtn = document.getElementById('events-sort-btn');
    if (!sortMenu || !sortBtn) return;

    sortMenu.querySelectorAll('.events-sort-item').forEach(i => {
      i.classList.toggle('active', i.dataset.sort === sortValue);
    });

    const activeItem = sortMenu.querySelector(`.events-sort-item[data-sort="${CSS.escape(sortValue)}"]`);
    const sortLabel = sortBtn.querySelector('span');
    if (sortLabel && activeItem) {
      sortLabel.textContent = activeItem.textContent;
    }
  }

  scrollToSection(sectionEl) {
    const nav = document.getElementById('events-tabs-nav');
    if (!sectionEl || !nav) return;
    const stickyTop = parseInt(getComputedStyle(nav).top) || 0;
    window.scrollTo({
      top: sectionEl.getBoundingClientRect().top + window.scrollY - stickyTop - nav.offsetHeight - 16,
      behavior: 'smooth'
    });
  }

  initShowAllLinks() {
    this._showAllHandlers = [];
    document.querySelectorAll('.events-section-more[data-filter-action]').forEach(link => {
      const handler = async (e) => {
        e.preventDefault();
        const action = link.dataset.filterAction;

        switch (action) {
          case 'highlight':
          case 'recommended':
          case 'trending':
            await this.applyFilter('sort', 'popularity');
            this.syncSortUI('popularity');
            break;
          case 'weekend':
            await this.applyFilter('date', 'weekend');
            break;
        }

        this.scrollToSection(document.getElementById('section-all'));
      };
      link.addEventListener('click', handler);
      this._showAllHandlers.push({ el: link, handler });
    });
  }

  updateFilterBadge() {
    if (this._suppressBadge) return;
    const badge = document.getElementById('events-filter-badge');
    const countEl = document.getElementById('events-filter-badge-count');
    if (!badge || !countEl) return;

    const f = this.state.filters;
    let count = 0;
    if (f.search) count++;
    if (f.category) count++;
    if (f.location) count++;
    if (f.date) count++;
    if (f.price) count++;

    countEl.textContent = count;

    if (count > 0) {
      badge.classList.add('visible');
    } else {
      badge.classList.remove('visible');
    }
  }

  // ============================================================
  // CLEANUP (Memory Leak Prevention)
  // ============================================================

  destroy() {
    // Disconnect IntersectionObservers
    if (this.infiniteScrollObserver) {
      this.infiniteScrollObserver.disconnect();
      this.infiniteScrollObserver = null;
    }
    if (this.scrollRevealObserver) {
      this.scrollRevealObserver.disconnect();
      this.scrollRevealObserver = null;
    }

    // Destroy Swiper instances
    Object.values(this.swipers).forEach(swiper => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    this.swipers = {};

    // Clear search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    // Clear API cache (prevent memory growth)
    EventsAPI.cache.clear();

    // Remove back-to-top button
    const backToTop = document.querySelector('.events-back-to-top');
    if (backToTop) {
      backToTop.remove();
    }

    // Remove filter badge listener
    const filterBadgeClearBtn = document.getElementById('events-filter-badge-clear');
    if (filterBadgeClearBtn && this._filterBadgeClearHandler) {
      filterBadgeClearBtn.removeEventListener('click', this._filterBadgeClearHandler);
      this._filterBadgeClearHandler = null;
    }

    // Remove show-all link listeners
    if (this._showAllHandlers) {
      this._showAllHandlers.forEach(({ el, handler }) => {
        el.removeEventListener('click', handler);
      });
      this._showAllHandlers = null;
    }

    // Remove global event listeners (Memory Leak Prevention)
    if (this._boundHandlers) {
      if (this._boundHandlers.documentClick) {
        document.removeEventListener('click', this._boundHandlers.documentClick);
      }
      if (this._boundHandlers.windowScroll) {
        window.removeEventListener('scroll', this._boundHandlers.windowScroll);
      }
      if (this._boundHandlers.windowScrollSpy) {
        window.removeEventListener('scroll', this._boundHandlers.windowScrollSpy);
      }
      if (this._boundHandlers.windowResize) {
        window.removeEventListener('resize', this._boundHandlers.windowResize);
      }
      if (this._boundHandlers.containerScroll && this._tabContainer) {
        this._tabContainer.removeEventListener('scroll', this._boundHandlers.containerScroll);
      }
      this._boundHandlers = null;
    }
    this._tabContainer = null;

    // Clear state
    this.state = null;
  }
}

// ============================================================
// GLOBAL FUNCTIONS
// ============================================================

window.toggleWatchlist = async function(eventId, buttonEl) {
  const app = window.eventsDiscoveryApp;
  const isCurrentlyWatched = app?.state.watchedEventIds.has(eventId);

  // Optimistic update
  if (app) {
    if (isCurrentlyWatched) {
      app.state.watchedEventIds.delete(eventId);
    } else {
      app.state.watchedEventIds.add(eventId);
    }
  }

  // Update button UI
  if (buttonEl) {
    buttonEl.classList.toggle('active', !isCurrentlyWatched);
    const svg = buttonEl.querySelector('svg');
    if (svg) {
      svg.setAttribute('fill', !isCurrentlyWatched ? 'currentColor' : 'none');
    }
  }

  // API call
  const result = await EventsAPI.toggleWatchlist(eventId, !isCurrentlyWatched);

  if (result.success) {
    window.showToast?.(
      !isCurrentlyWatched ? 'Zu Merkliste hinzugefügt' : 'Von Merkliste entfernt',
      !isCurrentlyWatched ? 'success' : 'info'
    );
  } else {
    // Rollback
    if (app) {
      if (isCurrentlyWatched) {
        app.state.watchedEventIds.add(eventId);
      } else {
        app.state.watchedEventIds.delete(eventId);
      }
    }
    if (buttonEl) {
      buttonEl.classList.toggle('active', isCurrentlyWatched);
      const svg = buttonEl.querySelector('svg');
      if (svg) {
        svg.setAttribute('fill', isCurrentlyWatched ? 'currentColor' : 'none');
      }
    }
    window.showToast?.('Fehler beim Aktualisieren', 'error');
  }
};

window.addToCalendar = function(eventId, title, startDate, location) {
  if (!startDate) {
    window.showToast?.('Kein Datum verfügbar', 'error');
    return;
  }

  const date = new Date(startDate);
  const formatDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const endDate = new Date(date.getTime() + 2 * 60 * 60 * 1000);

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Cita Events//DE',
    'BEGIN:VEVENT',
    `DTSTART:${formatDate(date)}`,
    `DTEND:${formatDate(endDate)}`,
    `SUMMARY:${title}`,
    location ? `LOCATION:${location}` : '',
    `URL:${window.location.origin}/event/${eventId}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter(Boolean).join('\r\n');

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  link.click();
  URL.revokeObjectURL(url);

  window.showToast?.('Kalendereintrag heruntergeladen', 'success');
};

window.shareEvent = async function(slug, title) {
  const url = `${window.location.origin}/event/${slug}`;

  if (navigator.share) {
    try {
      await navigator.share({ title, url });
      return;
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Share failed:', err);
    }
  }

  try {
    await navigator.clipboard.writeText(url);
    window.showToast?.('Link kopiert', 'success');
  } catch {
    await dialog({
      title: 'Link kopieren',
      description: 'Kopiere diesen Link manuell:',
      confirmLabel: 'OK',
      cancelLabel: null,
      tone: 'info',
      input: {
        defaultValue: url,
        placeholder: 'Event-Link'
      }
    });
  }
};

// ============================================================
// GLOBAL EVENT DELEGATION (XSS-safe)
// ============================================================

// Watchlist button clicks via event delegation (prevents XSS from inline handlers)
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.event-card-watchlist');
  if (btn) {
    e.preventDefault();
    e.stopPropagation();
    const eventId = btn.dataset.eventId;
    if (eventId) {
      window.toggleWatchlist(eventId, btn);
    }
  }
});

// ============================================================
// INITIALIZE
// ============================================================

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.eventsDiscoveryApp = new EventDiscoveryApp();
  });
} else {
  window.eventsDiscoveryApp = new EventDiscoveryApp();
}
